import React, { useMemo } from "react";
import useThemeMode from "../../hooks/useThemeMode";
import { getMarketingTheme } from "../../utils/marketingTheme";
import useI18n from "../../i18n/useI18n";

const PublicPricingFAQ = () => {
  const { isDark } = useThemeMode();
  const { t } = useI18n();
  const colors = useMemo(() => getMarketingTheme(isDark), [isDark]);

  const tt = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  const faqs = useMemo(
    () => [
      {
        question: tt(
          "pricingFaq.items.freePlan.question",
          "Can I start with the Free plan?"
        ),
        answer: tt(
          "pricingFaq.items.freePlan.answer",
          "Yes. The Free plan is designed to help you try Hookora, explore the workflow, and see how the tools fit your content process before upgrading."
        ),
      },
      {
        question: tt(
          "pricingFaq.items.moveToCreator.question",
          "When should I move to Creator?"
        ),
        answer: tt(
          "pricingFaq.items.moveToCreator.answer",
          "Creator is best when you are publishing more consistently and want more generations, unlimited saved hooks, and stronger workflow support."
        ),
      },
      {
        question: tt("pricingFaq.items.whoIsProFor.question", "Who is Pro for?"),
        answer: tt(
          "pricingFaq.items.whoIsProFor.answer",
          "Pro is best for serious creators and power users who want higher limits, more flexibility, and deeper workflow value as Hookora continues to grow."
        ),
      },
      {
        question: tt(
          "pricingFaq.items.upgradeLater.question",
          "Can I upgrade later?"
        ),
        answer: tt(
          "pricingFaq.items.upgradeLater.answer",
          "Yes. You can start free and upgrade when your content workflow needs more room."
        ),
      },
      {
        question: tt(
          "pricingFaq.items.moreThanOneTool.question",
          "Does Hookora include more than one tool?"
        ),
        answer: tt(
          "pricingFaq.items.moreThanOneTool.answer",
          "Yes. Hookora includes hooks, title optimization, captions, hashtags, description rewriting, short scripts, thumbnail text analysis, and more."
        ),
      },
      {
        question: tt(
          "pricingFaq.items.beginnerCreators.question",
          "Is Hookora built for beginner creators too?"
        ),
        answer: tt(
          "pricingFaq.items.beginnerCreators.answer",
          "Yes. Hookora is designed to feel useful whether you are just starting or already creating content regularly."
        ),
      },
    ],
    [t]
  );

  return (
    <section style={{ padding: "8px 20px 84px", background: colors.sectionBg }}>
      <div style={{ maxWidth: "1000px", margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <h2
            style={{
              margin: "0 0 10px",
              color: colors.text,
              fontSize: "clamp(1.9rem, 4vw, 2.8rem)",
              lineHeight: "1.1",
            }}
          >
            {tt("pricingFaq.title", "Pricing FAQ")}
          </h2>
          <p
            style={{
              margin: 0,
              color: colors.textMuted,
              fontSize: "16px",
              lineHeight: "1.7",
            }}
          >
            {tt(
              "pricingFaq.subtitle",
              "A few quick answers to common questions about Hookora pricing."
            )}
          </p>
        </div>

        <div style={{ display: "grid", gap: "14px" }}>
          {faqs.map((faq) => (
            <div
              key={faq.question}
              style={{
                background: colors.cardBg,
                border: `1px solid ${colors.border}`,
                borderRadius: "20px",
                padding: "20px",
                boxShadow: colors.shadowSoft,
                backdropFilter: isDark ? "blur(12px)" : "none",
              }}
            >
              <h3
                style={{
                  margin: "0 0 10px",
                  color: colors.text,
                  fontSize: "18px",
                  lineHeight: "1.35",
                }}
              >
                {faq.question}
              </h3>
              <p
                style={{
                  margin: 0,
                  color: colors.textMuted,
                  fontSize: "15px",
                  lineHeight: "1.75",
                }}
              >
                {faq.answer}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PublicPricingFAQ;